<template>
  <ReportPanel>
    <h6 class="q-mt-md q-mb-none">Please select a client and a date range for their responses to generate a narrative report.</h6>
    <p>The generation of the report may take upwards of 30 seconds depending on the complexity of the form responses.</p>

    <strong>Narrative Report Type</strong>
    <q-select
      style="width: 50%"
      outlined
      label="Select report type"
      dense
      v-model="selectedAssistant"
      :options="availableAssistants"
      option-label="label"
    >
      <template v-slot:option="scope">
        <q-item v-bind="scope.itemProps">
          <q-item-section>
            <q-item-label>{{ scope.opt.label }}</q-item-label>
            <q-item-label caption>{{ scope.opt.subtext }}</q-item-label>
          </q-item-section>
        </q-item>
      </template>
    </q-select>

    <strong>Client Select</strong>
    <div class="row full-width q-gutter-r-md items-center align-center">
      <q-select
        style="width: 50%"
        outlined
        label="Search for a client"
        placeholder="Name, NHI, email, phone, date of birth or ID"
        dense
        v-model="selectedUser"
        :options="filteredUserList"
        option-label="full_name"
        :loading="loadingUser"
        use-input
        @filter="filterFn"
      />
      <q-btn
        v-if="selectedUser"
        class="q-ml-md"
        label="View previous reports"
        outline
        color="primary"
        no-caps
        @click="previousReports = true"
      />
    </div>

    <q-dialog v-model="previousReports">
      <NarrativeReports :clientId="selectedUser.client_id" @close="previousReports = false" />
    </q-dialog>

    <strong>Date range of responses</strong>
    <div class="row full-width q-gutter-r-md q-mb-lg items-center">
      <q-input
        v-model="startDate"
        label="After ..."
        stack-label
        type="date"
        bg-color="white"
        outlined
        dense
        class="col-6"
      />

      <q-input
        v-model="endDate"
        label="Before ..."
        stack-label
        type="date"
        dense
        bg-color="white"
        outlined
        class="col-6"
      />
    </div>

    <q-btn
      :disabled="!selectedAssistant || !selectedUser"
      :loading="loadingNarrative"
      label="Generate Narrative Report"
      class="full-width"
      color="secondary"
      no-caps
      @click="generateNarrativeReport"
    />

    <div v-if="narrativeText" class="q-pa-md" style="min-width: 750px">
      <q-input
        v-model="narrativeTextTitle"
        label="Title"
        bg-color="white"
        outlined
        autogrow
      />

      <q-input
        v-model="narrativeText.content"
        bg-color="white"
        filled
        autogrow
      />
      <q-btn
        label="Save Narrative Report"
        color="primary"
        no-caps
        class="q-mt-md"
        @click="saveReport"
      />
    </div>
  </ReportPanel>
</template>

<script>
import api from "@/services/api/api";
import notify from "@/services/util/notify";
import NarrativeReports from "../NarrativeReports.vue";
import ReportPanel from "./ReportPanel.vue";
import { createPauaClientMatcher } from "@/services/util/pauaClientSearch";

export default {
  name: "NarrativeReportPanel",
  components: {
    NarrativeReports,
    ReportPanel
  },
  data() {
    return {
      startDate: null,
      endDate: null,
      availableAssistants: [
        {
          label: "Narrative Report",
          subtext: "Generates a clinical narrative report for the selected client, used in the Outcomes reporting process.",
          value: "REPORTING_NARRATIVE_ASSISTANT_ID"
        },
        {
          label: "Use Case Report",
          subtext: "Provides a general overview of the client's journey through the service.",
          value: "NORMAL_NARRATIVE_ASSISTANT_ID"
        }
      ],
      selectedAssistant: null,
      userList: [],
      filteredUserList: [],
      selectedUser: null,
      loadingUser: false,
      loadingNarrative: false,
      narrativeText: null,
      narrativeTextTitle: null,
      previousReports: false
    }
  },
  async created() {
    await this.retrieveClientList();
  },
  methods: {
    async retrieveClientList() {
      this.loadingUser = true;

      try {
        const response = await api.client.getClientsFormCompleted();
        this.userList = response.data;
        this.filteredUserList = response.data;
      } catch (error) {
        console.error(error);
        notify.error("Error retrieving client list");
      } finally {
        this.loadingUser = false;
      }
    },

    async generateNarrativeReport() {
      this.loadingNarrative = true;
      this.narrativeText = null;

      try {
        const defaultStartDate = this.startDate || "2000-01-01";
        const defaultEndDate = this.endDate || "2100-01-01";

        const payload = {
          client_id: this.selectedUser.client_id,
          start_date: defaultStartDate,
          end_date: defaultEndDate,
          assistant_id: this.selectedAssistant.value
        };

        const report = await api.reports.generateNarrativeReport(payload);
        this.narrativeText = report;
        this.narrativeTextTitle = `${this.selectedUser.full_name}`;
      } catch (error) {
        console.error(error);
        notify.error("There was an issue generating your narrative report.");
      } finally {
        this.loadingNarrative = false;
      }
    },

    async saveReport() {
      this.loadingNarrative = true;

      try {
        const payload = {
          title: this.narrativeTextTitle,
          clientId: this.selectedUser.client_id,
          content: this.narrativeText?.content,
          assistant: this.selectedAssistant.label,
          summary: this.narrativeText?.summary
        };

        await api.reports.saveNarrativeReport(payload);
        notify.primary("Narrative report saved successfully.", "top");
      } catch (error) {
        console.error(error);
        notify.error("There was an issue saving your narrative report.");
      } finally {
        this.loadingNarrative = false;
      }
    },

    filterFn(inputVal, update) {
      update(() => {
        this.filteredUserList = this.userList.filter(createPauaClientMatcher(inputVal));
      });
    }
  }
}
</script>
